import React, {memo, useCallback} from 'react'
import {Typography} from '@material-ui/core';
import PropTypes from 'prop-types';
import {useDropzone} from 'react-dropzone';
import GreyBox from './GreyBox';
import GridBlock from './GridBlock';
import Tr from 'translations/Translations';
import clsx from 'clsx';
import makeStyles from "@material-ui/core/styles/makeStyles";
import WorkableButton from "./button/WorkableButton";

const useStyles = makeStyles(theme => ({
    root: {
        cursor: 'pointer'
    },
    defaultChildrenGrid: {
        '&>*:not(:first-child)': {
            marginLeft: theme.spacing(2),
        }
    }
}))

function Dropzone(
    {
        children,
        formikProps,
        id,
        multiple,
        submitOnChange,
        className,
        onChange = () => {
        },
        disabled,
        working,
        acceptExtensionList
    }) {
    const classes = useStyles();
    const isError = formikProps ? (!!formikProps.errors[id] && !!formikProps.touched[id]) : false;
    const helperText = formikProps ? (formikProps.errors[id] || ' ') : '';

    const onDrop = useCallback(acceptedFiles => {
        const files = acceptedFiles;
        onChange(files);

        if (formikProps) {
            formikProps.setFieldValue(id, files)
            if (submitOnChange) {
                formikProps.submitForm();
            }
        }
        // eslint-disable-next-line
    }, [onChange])

    const generateExtension = () => {
        if (acceptExtensionList === undefined) return ''
        return acceptExtensionList.map(x => "." + x).join(", ");
    }

    const {getRootProps, getInputProps} = useDropzone({onDrop, multiple, accept: generateExtension()})

    const setTouched = () => {
        if (formikProps && !formikProps.touched[id]) {
            formikProps.setFieldTouched(id, true)
        }
    }

    const {
        onClick,
        ...rootProps
    } = getRootProps({
        onDrop: setTouched,
        onBlur: setTouched,
    });

    const childrenComponent = () => {
        if (children === undefined) {
            return (
                <GridBlock
                    alignItems='center'
                    justify='center'
                    className={classes.defaultChildrenGrid}
                >
                    <Typography variant='body1' className={classes.text}>
                        {Tr('dropzone_text_1')}
                    </Typography>
                    <WorkableButton onClick={onClick} disabled={disabled || working} working={working}>
                        {Tr(working ? 'file_working' : 'select_file')}
                    </WorkableButton>
                    <Typography variant='body1' className={classes.text}>
                        {Tr('dropzone_text_2')}
                    </Typography>
                </GridBlock>
            )
        }
        if (typeof children === 'string') {
            return (
                <Typography variant='body1' className={classes.text}>
                    {children}
                </Typography>
            )
        }
        return children;
    }

    return (
        <div {...rootProps} className={clsx(classes.root, className)}>
            <input {...getInputProps({
                onBlur: setTouched,
            })} />
            <GreyBox>
                {childrenComponent()}
                {isError &&
                    <Typography variant='body1' color='error'>
                        {helperText}
                    </Typography>
                }
            </GreyBox>
        </div>
    )
}

Dropzone.propTypes = {
    classes: PropTypes.any,
    formikProps: PropTypes.object,
    id: PropTypes.string,
    multiple: PropTypes.bool,
}

Dropzone.defaultProps = {
    multiple: false
}

export default memo(Dropzone, (prevProps, nextProps) => {
    if (prevProps.id !== nextProps.id) return false;
    if (prevProps.working !== nextProps.working) return false;
    const id = nextProps.id;
    if (prevProps.formikProps && nextProps.formikProps) {
        if (prevProps.formikProps.values[id] !== nextProps.formikProps.values[id]) return false;
        if (prevProps.formikProps.errors[id] !== nextProps.formikProps.errors[id]) return false;
        if (prevProps.formikProps.touched[id] !== nextProps.formikProps.touched[id]) return false;
    }
    if (JSON.stringify(prevProps.classes) !== JSON.stringify(nextProps.classes)) return false;
    if (prevProps.acceptExtensionList !== nextProps.acceptExtensionList) return false;
    if (!!prevProps.disabled !== !!nextProps.disabled) return false;
    return true;
})
